import React, { useState, useEffect, useRef} from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useTheme } from '@mui/material/styles';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid2';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';


interface Props {
  title: string;
  id: string;
  items: Array<PageItem>;
  colorInvert?: boolean;
}

const NavItem = ({
  title,
  id,
  items,
  colorInvert = false
}: Props): JSX.Element => {

  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [openedPopoverId, setOpenedPopoverId] = useState(null);
  
  const handleClick = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, popoverId: any) => {
    //setAnchorEl(null);
    //setOpenedPopoverId(null);
    //console.log('enter');
    if ('currentTarget' in event) {
          setAnchorEl(event.currentTarget);
      }
      setOpenedPopoverId(popoverId);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpenedPopoverId(null);// delay to prevent flicker
  };

  const [activeLink, setActiveLink] = useState('');
  

  useEffect(() => {
    setActiveLink(window && window.location ? window.location.pathname : '');
  }, []);

  const hasActiveLink = () => items.find((i) => i.href === activeLink);
  const linkColor = 'text.primary';

  //add support for keyboard navigation of popover menu items
  const linksRef = useRef<(HTMLButtonElement | HTMLAnchorElement | null)[]>([]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>, index: number) => {
    
    const links = linksRef.current;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      const next = (index + 1) % links.length;
      links[next]?.focus();
    };

    if (event.key === "ArrowUp") {
      event.preventDefault(); 
      const prev = (index - 1 + links.length) % links.length;
      links[prev]?.focus();
    };

  };


  return (
    <Box>
      <Box
        tabIndex={0}
        display={'flex'}
        alignItems={'center'}
        p={0.5}
        aria-label={title}
        sx={{ 
          cursor: 'pointer',
          "&:hover": {
            borderBottomColor: theme.palette.secondary.dark,
            borderBottomWidth: 3,
            borderBottomStyle: "solid",
            },
        }}
        //onMouseEnter ={(e) => handleClick(e, id)}
        onClick={(e) => handleClick(e, id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleClick(e, id);
          }
        }}
      >
        <Typography
          fontWeight={700}
          variant='h6'
          //sx={{fontSize: "1.1rem"}}
          color={hasActiveLink() ? 'secondary.dark' : 'text.ocpfBlue'}
        >
          {title}
        </Typography>
        <ExpandMoreIcon
          sx={{
            marginLeft: theme.spacing(1 / 4),
            width: 16,
            height: 16,
            transform: openedPopoverId === id ? 'rotate(180deg)' : 'none',
            color: linkColor,
          }}
        />
      </Box>
      <Popover
        elevation={3}
        id={id}
        open={openedPopoverId === id}
        anchorEl={anchorEl}
        onClose={handleClose}
        closeAfterTransition={true}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        disableRestoreFocus={false} // ensures focus goes back to the button when closed
        sx={{
          '.MuiPaper-root': {
            maxWidth: items.length > 12 ? 350 : 250,
            padding: 2,
            marginTop: 2,
            borderTopRightRadius: 0,
            borderTopLeftRadius: 0,
            borderBottomRightRadius: 8,
            borderBottomLeftRadius: 8,
            borderTop: `3px solid ${theme.palette.primary.main}`,
          }
        }}
      >
        <Grid container spacing={0.5}>
          {items.map((p, i) => (
            <Grid 
              key={i} 
              size={{xs: items.length > 12 ? 6 : 12}}
              >
              <Button
                tabIndex={0}
                component={'a'}
                href={p.href}
                fullWidth
                ref={(el) => (linksRef.current[i] = el)}
                onKeyDown={(e) => handleKeyDown(e, i)}
                sx={{
                  justifyContent: 'flex-start',
                  color:
                    activeLink === p.href
                      ? theme.palette.secondary.dark
                      : theme.palette.text.primary,
                  backgroundColor:'transparent',
                  fontWeight: activeLink === p.href ? 600 : 400,
                  padding:0.4,
                  "&:hover": {
                    textDecoration: "underline",
                    color: "primary.main",
                  },
                }}
              >
                {p.title}
              </Button>
            </Grid>
          ))}
        </Grid>
      </Popover>
    </Box>
  );
};

export default NavItem;
